import type {ButtonProps} from '@primer/react'
import {Button} from '@primer/react'

import {type CommandId, getCommandMetadata} from '../commands'
import {useCommandsContext} from '../commands-context'
import {CommandKeybindingHint} from './CommandKeybindingHint'

export interface CommandButtonProps extends ButtonProps {
  commandId: CommandId
  /** If `children` is not provided, the button will render the command name as its label by default. */
  children?: ButtonProps['children']
  /** If `true` and no `trailingVisual` is set, will render a keybinding hint as the trailing visual. */
  showKeybindingHint?: boolean

  ref?: React.Ref<HTMLButtonElement>
}

const ButtonKeybindingHint = ({commandId}: {commandId: CommandId}) => (
  // This becomes part of the label which gets noisy (but we don't want to hide this info from screen reader users),
  // so we wrap in hidden parentheses to offset it a little and make it read better
  <>
    <span className="sr-only">(</span>
    <CommandKeybindingHint commandId={commandId} format="condensed" />
    <span className="sr-only">)</span>
  </>
)

/**
 * `CommandButton` is a wrapper around `@primer/react` `Button`, but instead of an `onClick` handler it takes a
 * command ID and handles clicks by emitting command trigger events.
 *
 * If the command is gated by a disabled feature flag, nothing will render.
 */
export const CommandButton = ({
  ref,
  commandId,
  children,
  trailingVisual,
  showKeybindingHint = false,
  onClick: externalOnClick,
  ...forwardProps
}: CommandButtonProps) => {
  const metadata = getCommandMetadata(commandId)
  const {triggerCommand} = useCommandsContext()

  if (!metadata) return null

  return (
    <Button
      {...forwardProps}
      onClick={event => {
        externalOnClick?.(event)
        if (!event.defaultPrevented) triggerCommand(commandId, event.nativeEvent)
      }}
      trailingVisual={
        trailingVisual ?? (showKeybindingHint ? <ButtonKeybindingHint commandId={commandId} /> : undefined)
      }
      ref={ref}
    >
      {children ?? metadata.name}
    </Button>
  )
}
CommandButton.displayName = 'CommandButton'
